import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Card, CardContent, CardHeader, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import {
  useGetCharterHundredDollarPromotionList,
  useUpsertCharterHundredDollarPromotion,
} from '~/hooks/apis/flights-charter/useCharterHundredDollarPromotion';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type {
  CharterPromotionPricingInventoryRow,
  UpsertCharterPromotionPricingInventoryBody,
} from '~/types/charterPromotionPricingInventory';

import { charterAirportOptions } from '../FriendAndFamily/charterAirports';

import CharterHundredDollarPromotionDialog, {
  type HundredDollarPromotionFormValues,
} from './CharterHundredDollarPromotionDialog';

const pageSizeOptions = [5, 10, 25];
const tableHeight = 452;
const defaultPromotionDepDate = '2026-05-18';

const emptyPromotionFormValues: HundredDollarPromotionFormValues = {
  depAirport: 'MEL',
  arrAirport: 'MLE',
  depDate: defaultPromotionDepDate,
  availableDaysBefore: '',
  promotionAllocation: '',
  adultPrice: '',
  childPrice: '',
};

function getAlternateAirport(airport: string): string {
  return airport === charterAirportOptions[0] ? charterAirportOptions[1] : charterAirportOptions[0];
}

function normalizePromotionAirports(values: HundredDollarPromotionFormValues): HundredDollarPromotionFormValues {
  const depAirport = charterAirportOptions.includes(values.depAirport as (typeof charterAirportOptions)[number])
    ? values.depAirport
    : charterAirportOptions[0];
  const arrAirport =
    depAirport === values.arrAirport
      ? getAlternateAirport(depAirport)
      : values.arrAirport || getAlternateAirport(depAirport);

  return {
    ...values,
    depAirport,
    arrAirport,
  };
}

const tableCardHeaderSx = {
  p: 0,
  pb: 1,
  '& .MuiCardHeader-content': {
    my: 0,
  },
  '& .MuiCardHeader-action': {
    m: 0,
    alignSelf: 'center',
  },
};

function toPromotionFormValues(row: CharterPromotionPricingInventoryRow): HundredDollarPromotionFormValues {
  return normalizePromotionAirports({
    depAirport: row.depAirport || 'MEL',
    arrAirport: row.arrAirport || 'MLE',
    depDate: row.date || defaultPromotionDepDate,
    availableDaysBefore: String(row.availableDaysBefore),
    promotionAllocation: String(row.promotionAllocation),
    adultPrice: String(row.adultPrice),
    childPrice: String(row.childPrice),
  });
}

function toPromotionPayload(values: HundredDollarPromotionFormValues): UpsertCharterPromotionPricingInventoryBody {
  return {
    depAirport: values.depAirport.trim(),
    arrAirport: values.arrAirport.trim(),
    depDate: values.depDate.trim(),
    cabinClass: 'ECONOMY',
    allocation: Number(values.promotionAllocation),
    availableDaysBefore: Number(values.availableDaysBefore),
    pricing: [
      { paxType: 'ADT', price: Number(values.adultPrice), tax: 0, currency: 'AUD' },
      { paxType: 'CHD', price: Number(values.childPrice), tax: 0, currency: 'AUD' },
    ],
  };
}

export default function CharterHundredDollarPromotionPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    page: 0,
    pageSize: 10,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState<HundredDollarPromotionFormValues>(emptyPromotionFormValues);
  const [editingRow, setEditingRow] = useState<CharterPromotionPricingInventoryRow | null>(null);
  const upsertPromotionMutation = useUpsertCharterHundredDollarPromotion();
  const promotionListQuery = useGetCharterHundredDollarPromotionList();

  const promotionRows = useMemo(() => promotionListQuery.data?.result ?? [], [promotionListQuery.data?.result]);

  const resetForm = useCallback(() => {
    setFormValues(normalizePromotionAirports(emptyPromotionFormValues));
  }, []);

  const handleFormChange = useCallback((field: keyof HundredDollarPromotionFormValues, value: string) => {
    setFormValues((current) => {
      if (field === 'depAirport') {
        return normalizePromotionAirports({
          ...current,
          depAirport: value,
          arrAirport: getAlternateAirport(value),
        });
      }

      if (field === 'arrAirport') {
        return normalizePromotionAirports({
          ...current,
          arrAirport: value,
          depAirport: getAlternateAirport(value),
        });
      }

      return {
        ...current,
        [field]: value,
      };
    });
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    setEditingRow(null);
    resetForm();
    setIsDialogOpen(true);
  }, [resetForm]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingRow(null);
    resetForm();
  }, [resetForm]);

  const handleUpsertPromotion = useCallback(async () => {
    const payload = toPromotionPayload(formValues);

    try {
      await upsertPromotionMutation.mutateAsync(payload);
      enqueueSnackbar(editingRow ? 'Promotion row updated.' : 'Promotion row created.', {
        variant: 'success',
      });
      handleCloseDialog();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save promotion row.', {
        variant: 'error',
      });
    }
  }, [editingRow, enqueueSnackbar, formValues, handleCloseDialog, upsertPromotionMutation]);

  const handleEditRow = useCallback((row: CharterPromotionPricingInventoryRow) => {
    setEditingRow(row);
    setFormValues(toPromotionFormValues(row));
    setIsDialogOpen(true);
  }, []);

  const columns = useMemo<Array<GridColDef<CharterPromotionPricingInventoryRow>>>(
    () => [
      { field: 'date', headerName: 'Date', minWidth: 120, flex: 0.8 },
      { field: 'route', headerName: 'Route', minWidth: 140, flex: 0.9 },
      { field: 'cabin', headerName: 'Cabin', minWidth: 120, flex: 0.8 },
      { field: 'availableDaysBefore', headerName: 'Available Days Before', type: 'number', minWidth: 180, flex: 1 },
      { field: 'promotionAllocation', headerName: 'Promotion Allocation', type: 'number', minWidth: 180, flex: 1 },
      { field: 'adultPrice', headerName: 'ADT Price', type: 'number', minWidth: 120, flex: 0.8 },
      { field: 'childPrice', headerName: 'CHD Price', type: 'number', minWidth: 120, flex: 0.8 },
      { field: 'totalBooked', headerName: 'Total Booked', type: 'number', minWidth: 130, flex: 0.8 },
      { field: 'openForSale', headerName: 'Open for Sale', type: 'number', minWidth: 130, flex: 0.8 },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 90,
        renderCell: ({ row }) => (
          <Button
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              handleEditRow(row);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [handleEditRow],
  );

  return (
    <Stack spacing={3}>
      <Card>
        <CardHeader
          title={
            <Stack spacing={0.5}>
              <Typography variant="h6">Hundred Dollar Promotion</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure economy-only promotion inventory and ADT/CHD pricing for selected charter departures.
              </Typography>
            </Stack>
          }
          action={
            <Button variant="contained" onClick={handleOpenCreateDialog}>
              Add Promotion Row
            </Button>
          }
          sx={tableCardHeaderSx}
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Infants do not consume promotion inventory and keep their standard charter pricing.
          </Alert>
          <Box sx={{ height: tableHeight, width: '100%' }}>
            <DataGrid
              rows={promotionRows}
              columns={columns}
              loading={promotionListQuery.isLoading || promotionListQuery.isFetching}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={pageSizeOptions}
              slots={{ pagination: GridPagination }}
              disableRowSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      <CharterHundredDollarPromotionDialog
        open={isDialogOpen}
        loading={upsertPromotionMutation.isPending}
        values={formValues}
        title={editingRow ? 'Edit Hundred Dollar Promotion Row' : 'Add Hundred Dollar Promotion Row'}
        submitLabel={editingRow ? 'Update' : 'Create'}
        existingRows={promotionRows}
        isEditMode={Boolean(editingRow)}
        minimumPromotionAllocation={editingRow?.totalBooked ?? 0}
        onClose={handleCloseDialog}
        onSubmit={handleUpsertPromotion}
        onChange={handleFormChange}
      />
    </Stack>
  );
}
